import type { InputHTMLAttributes } from 'react'
import { forwardRef } from 'react'
import { CloseIcon, SearchIcon } from './icons'

export interface SearchBoxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'onSubmit'> {
  value: string
  onChange: (value: string) => void
  /** Enter / the keyboard's search key. */
  onSubmit?: (value: string) => void
  /** Accessible name. Default "Search". */
  label?: string
}

/**
 * A search field with a leading glyph and a clear button. `type="search"` and
 * `enterKeyHint="search"` give phones the right keyboard; Escape clears.
 */
export const SearchBox = forwardRef<HTMLInputElement, SearchBoxProps>(function SearchBox(
  { value, onChange, onSubmit, label = 'Search', className, placeholder, ...rest },
  ref,
) {
  return (
    <div className={`rs-search ${className ?? ''}`} role="search">
      <span className="rs-search__icon" aria-hidden="true">
        <SearchIcon size={20} />
      </span>
      <input
        {...rest}
        ref={ref}
        type="search"
        enterKeyHint="search"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        className="rs-input rs-search__input"
        aria-label={label}
        placeholder={placeholder ?? label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit?.(value)
          if (e.key === 'Escape' && value) {
            e.stopPropagation()
            onChange('')
          }
          rest.onKeyDown?.(e)
        }}
      />
      {value ? (
        <button
          type="button"
          className="rs-search__clear"
          aria-label="Clear search"
          onClick={() => onChange('')}
        >
          <CloseIcon size={18} />
        </button>
      ) : null}
    </div>
  )
})
